import { baseApi, isTransportError, resourceScopeTag, resourceTag } from '@krp/client-runtime'

import { PLATFORM_ACCESS_TAG } from '../../access/api/accessApi'
import type { ConnectorManifest } from '../model/connectors'
import type { MyResource, ResourceDetails, ResourceDraft, ResourceSummary } from '../model/types'
import {
  createResourceWire,
  decodeConnectors,
  decodeMyResources,
  decodeProbeResult,
  decodeResourceDetails,
  decodeResources,
  updateResourceWire,
  type ProbeResult,
} from './contracts'
import { takeSecret } from './secretHandoff'
import { PLATFORM_RESOURCES_URLS } from './urls'

/**
 * Endpoints фичи в ОБЩЕМ `baseApi` (R04): имена с префиксом пакета
 * (`platform*`), свой тип тега. Ответ проверяется декодером в transport,
 * компонент DTO не видит (R06).
 */
export const PLATFORM_RESOURCE_TAG = 'PlatformResource' as const

export interface PlatformResourceArgs {
  resourceId: string
}

/** Мои ресурсы с правом: право обязательно, вид — по желанию. */
export interface PlatformMyResourcesArgs {
  capability: string
  kind?: string
}

/**
 * Аргументы команд с секретом: в аргументах только ССЫЛКА на отложенный
 * секрет (`secretHandoff`), а не значение — аргументы мутации живут в Redux.
 */
export interface PlatformCreateResourceArgs {
  kind: string
  contractVersion: string
  draft: ResourceDraft
  secretRef: string | null
}

export interface PlatformUpdateResourceArgs {
  resourceId: string
  draft: ResourceDraft
  secretRef: string | null
}

const LIST_TAG = { type: PLATFORM_RESOURCE_TAG, id: 'LIST' } as const
/** Виды и их манифесты меняются только установкой Platform: ресурсы их не затрагивают. */
const CONNECTORS_TAG = { type: PLATFORM_RESOURCE_TAG, id: 'CONNECTORS' } as const
/** «Мои ресурсы» зависят и от реестра, и от прав: правка любого — перечитать. */
export const MINE_TAG = { type: PLATFORM_RESOURCE_TAG, id: 'MINE' } as const
const MY_ACCESS_TAG = { type: PLATFORM_ACCESS_TAG, id: 'MINE' } as const

export const resourcesApi = baseApi.enhanceEndpoints({ addTagTypes: [PLATFORM_RESOURCE_TAG, PLATFORM_ACCESS_TAG] }).injectEndpoints({
  endpoints: (build) => ({
    /**
     * Мои ресурсы, где решение прав разрешает право (`me/resources`, AU1b): сервер
     * уже проверил всё — включён ли ресурс, объявлено и включено ли право, выдано
     * ли оно роли и допущена ли роль к ресурсу. Реестрового права не требует.
     */
    platformMyResources: build.query<readonly MyResource[], PlatformMyResourcesArgs>({
      query: ({ capability, kind }) => ({
        url: PLATFORM_RESOURCES_URLS.mine,
        params: { capability, ...(kind !== undefined ? { kind } : {}) },
        decode: decodeMyResources,
      }),
      providesTags: (result) => [
        MINE_TAG,
        MY_ACCESS_TAG,
        ...(result ?? []).map((resource) => resourceScopeTag(resource.id)),
      ],
    }),
    /** Установленные виды с манифестами (права и модули): чтение для любого вошедшего, сервер ничего не скрывает. */
    platformConnectors: build.query<readonly ConnectorManifest[], void>({
      query: () => ({ url: PLATFORM_RESOURCES_URLS.connectors, decode: decodeConnectors }),
      providesTags: [CONNECTORS_TAG],
    }),
    platformResources: build.query<readonly ResourceSummary[], void>({
      query: () => ({ url: PLATFORM_RESOURCES_URLS.list, decode: decodeResources }),
      providesTags: (result) => [
        LIST_TAG,
        ...(result ?? []).map((resource) => resourceTag(PLATFORM_RESOURCE_TAG, resource.id, 'SUMMARY')),
      ],
    }),
    /**
     * Один ресурс: rid — аргумент запроса, значит и часть ключа кэша (D-064).
     * Несёт native-настройки для формы правки; секрета в них нет.
     */
    platformResource: build.query<ResourceDetails, PlatformResourceArgs>({
      query: ({ resourceId }) => ({ url: PLATFORM_RESOURCES_URLS.one(resourceId), decode: decodeResourceDetails }),
      providesTags: (_result, _error, { resourceId }) => [
        resourceTag(PLATFORM_RESOURCE_TAG, resourceId, 'SUMMARY'),
        resourceScopeTag(resourceId),
      ],
    }),
    /** Добавить ресурс (`registry.write`). Секрет забирается из `secretHandoff` один раз. */
    platformCreateResource: build.mutation<ResourceDetails, PlatformCreateResourceArgs>({
      query: ({ kind, contractVersion, draft, secretRef }) => ({
        url: PLATFORM_RESOURCES_URLS.list,
        method: 'POST',
        body: createResourceWire(kind, contractVersion, draft, takeSecret(secretRef)),
        decode: decodeResourceDetails,
      }),
      // Исход неизвестен (ответа нет) — ресурс мог создаться: список перечитывается, чтобы повтор не завёл второй.
      invalidatesTags: (result, error) =>
        result !== undefined || (isTransportError(error) && error.kind === 'unknown-outcome') ? [LIST_TAG, MINE_TAG] : [],
    }),
    /**
     * Изменить ресурс (`registry.write` + rid). Новые настройки меняют смысл всего,
     * что native-служба отдавала по этому rid, — перечитывается вся его область.
     */
    platformUpdateResource: build.mutation<ResourceDetails, PlatformUpdateResourceArgs>({
      query: ({ resourceId, draft, secretRef }) => ({
        url: PLATFORM_RESOURCES_URLS.one(resourceId),
        method: 'PUT',
        body: updateResourceWire(draft, takeSecret(secretRef)),
        decode: decodeResourceDetails,
      }),
      invalidatesTags: (result, _error, { resourceId }) =>
        result === undefined
          ? []
          : [LIST_TAG, MINE_TAG, resourceTag(PLATFORM_RESOURCE_TAG, resourceId, 'SUMMARY'), resourceScopeTag(resourceId)],
    }),
    /** Проверка связи Platform: команда, не чтение — результат не кэшируется и не повторяется сам. */
    platformProbeResource: build.mutation<ProbeResult, PlatformResourceArgs>({
      query: ({ resourceId }) => ({ url: PLATFORM_RESOURCES_URLS.probe(resourceId), method: 'POST', decode: decodeProbeResult }),
    }),
  }),
})

export const {
  platformConnectors,
  platformMyResources,
  platformResources,
  platformResource,
  platformCreateResource,
  platformUpdateResource,
  platformProbeResource,
} = resourcesApi.endpoints
